/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-24
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Paper,
} from '@mui/material';
import {
  Close,
  Email,
  Person,
  AccessTime,
  Send,
  CheckCircle,
  Cancel,
  Schedule,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { Invitation, InvitationStatus } from '../../../types/invitation';
import { getRoleDisplayName, getRoleColor } from '../../../utils/permissions';
import InvitationStatusBadge from './InvitationStatusBadge';

interface InvitationDetailDialogProps {
  invitation: Invitation | null;
  open: boolean;
  onClose: () => void;
}

/**
 * InvitationDetailDialog component displays comprehensive invitation details
 * in a modal dialog format.
 */
const InvitationDetailDialog = ({
  invitation,
  open,
  onClose,
}: InvitationDetailDialogProps) => {
  const { t } = useTranslation('studio');

  if (!invitation) {
    return null;
  }

  const formatDate = (date: Date) =>
    new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const formatRelativeTime = (date: Date) => {
    const now = new Date();
    const diffInDays = Math.ceil(
      (new Date(date).getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffInDays < 0) {
      return t('invitationCard.expiredDaysAgo', {
        days: Math.abs(diffInDays),
        count: Math.abs(diffInDays),
      });
    }
    if (diffInDays === 0) {
      return t('invitationCard.expiresToday');
    }
    if (diffInDays === 1) {
      return t('invitationCard.expiresTomorrow');
    }
    return t('invitationCard.expiresIn', {
      days: diffInDays,
      count: diffInDays,
    });
  };

  const getStatusIcon = (status: InvitationStatus) => {
    switch (status) {
      case InvitationStatus.PENDING:
        return <Schedule color="warning" />;
      case InvitationStatus.DECLINED:
        return <Cancel color="error" />;
      case InvitationStatus.EXPIRED:
        return <AccessTime color="error" />;
      case InvitationStatus.CANCELLED:
        return <Cancel color="disabled" />;
      default:
        return <Schedule color="action" />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Email color="primary" />
          <Typography variant="h6" component="span">
            {t('invitationDialog.title')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          {/* Recipient Information */}
          <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              {t('invitationDialog.sections.recipientInfo')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {t('invitationDialog.labels.email')}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {invitation.email}
                </Typography>
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {t('invitationDialog.labels.role')}
                  </Typography>
                </Box>
                <Chip
                  label={getRoleDisplayName(invitation.role)}
                  size="small"
                  sx={{
                    bgcolor: getRoleColor(invitation.role),
                    color: 'white',
                    fontWeight: 500,
                    mt: 0.5,
                  }}
                />
              </Box>
            </Box>
          </Paper>

          {/* Status Information */}
          <Paper sx={{ p: 3 }}>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              {t('invitationDialog.sections.statusTiming')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    {getStatusIcon(invitation.status)}
                    <Typography variant="body2" color="text.secondary">
                      {t('invitationDialog.labels.currentStatus')}
                    </Typography>
                  </Box>
                  <InvitationStatusBadge
                    status={invitation.status}
                    expiresAt={invitation.expiresAt}
                    size="medium"
                  />
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Send fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {t('invitationDialog.labels.sent')}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {formatDate(invitation.sentAt)}
                  </Typography>
                </Box>
              </Box>
              {invitation.status === InvitationStatus.PENDING && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTime fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {t('invitationDialog.labels.expiration')}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {formatDate(invitation.expiresAt)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {formatRelativeTime(invitation.expiresAt)}
                  </Typography>
                </Box>
              )}
              {invitation.acceptedAt && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle fontSize="small" color="success" />
                    <Typography variant="body2" color="text.secondary">
                      {t('invitationDialog.labels.accepted')}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {formatDate(invitation.acceptedAt)}
                  </Typography>
                </Box>
              )}
              {invitation.declinedAt && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Cancel fontSize="small" color="error" />
                    <Typography variant="body2" color="text.secondary">
                      {t('invitationDialog.labels.declined')}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {formatDate(invitation.declinedAt)}
                  </Typography>
                </Box>
              )}
              {invitation.cancelledAt && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Cancel fontSize="small" color="disabled" />
                    <Typography variant="body2" color="text.secondary">
                      {t('invitationDialog.labels.cancelled')}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {formatDate(invitation.cancelledAt)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Custom Message */}
          {invitation.message && (
            <Paper sx={{ p: 3 }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                {t('invitationDialog.sections.customMessage')}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontStyle: 'italic',
                  color: 'text.secondary',
                  bgcolor: 'grey.50',
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                &ldquo;{invitation.message}&rdquo;
              </Typography>
            </Paper>
          )}

          {/* Invitation Source */}
          <Paper sx={{ p: 3 }}>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              {t('invitationDialog.sections.invitationDetails')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('invitationDialog.labels.invitedBy')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                    {invitation.firstName && invitation.lastName
                      ? `${invitation.firstName} ${invitation.lastName}`
                      : invitation.invited_by_name || 'Unknown'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('invitationDialog.labels.studio')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                    {invitation.studioName}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('invitationDialog.labels.invitationId')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    bgcolor: 'grey.100',
                    p: 1,
                    borderRadius: 0.5,
                    mt: 0.5,
                  }}
                >
                  {invitation.id}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} startIcon={<Close />} color="inherit">
          {t('invitationDialog.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvitationDetailDialog;
